import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export const ACTION_NOTICE_DURATION_MS = 4_000;

type Notice = {
  id: number;
  message: string;
};

const ActionNoticeContext = createContext<(message: string) => void>(() => undefined);

export function ActionNoticeProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const nextId = useRef(0);
  const timer = useRef<number | null>(null);

  const showNotice = useCallback((message: string) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    const id = ++nextId.current;
    setNotice({ id, message });
    timer.current = window.setTimeout(() => {
      setNotice((current) => current?.id === id ? null : current);
      timer.current = null;
    }, ACTION_NOTICE_DURATION_MS);
  }, []);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  return (
    <ActionNoticeContext.Provider value={showNotice}>
      {children}
      <div className="action-notice-region no-print" role="status" aria-live="polite" aria-atomic="true">
        {notice && <div className="action-notice" key={notice.id}>{notice.message}</div>}
      </div>
    </ActionNoticeContext.Provider>
  );
}

export function useActionNotice() {
  return useContext(ActionNoticeContext);
}
